import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  useNotification,
  getButtonSpinner,
  showBackdropLoader,
} from 'utils/commonUtils';
import MDBox from 'components/MDBox';
import Grid from '@mui/material/Grid';
import {
  bookedSessionV2Columns,
  getBookedSessionV2ListData,
} from './ViewBookedSessionsTabV2Utils';
import { useForm } from 'react-hook-form';
import MDTypography from 'components/MDTypography';
import { GridWrapper } from 'components/Ui/styled';
import { addDays, format, isValid } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import {
  setDataGridCounts,
  resetDataGridCounts,
} from 'utils/commonSlice/dataGridCounts/dataGridCountsSlice';
import DeltaDataGrid from 'utils/CommonDataGrid/CommonDataGrid';
import StudentSearch from 'layouts/students/common/StudentSearch';
import BookedSessionInfoV2Dialog from './BookedSessionInfoV2Dialog';
import { FormInputDate } from 'components/Common/Forms/FormInputDate';
import BookedSessionStatusV2Dialog from './BookedSessionStatusV2Dialog';
import FormInputDropdown from 'components/Common/Forms/FormInputDropDown';
import { dateFormatYMD, intialPageSize, booleanOptions } from 'constants';
import {
  setSubject,
  setAscending,
  setLatestDate,
  setEarliestDate,
  setIncludeCancelled,
  resetBookedSessionFilter,
} from 'layouts/tutoring/store/bookedSessionFilterSlice/bookedSessionFilterSlice';
import { getV2Session } from 'layouts/tutoringV2/services/viewBookedSessionsV2Api';
import {
  setBookedSessionPagination,
  resetBookedSessionPagination,
  setBookedSessionPaginationPreviousKey,
} from 'layouts/tutoring/store/bookedSessionPaginationSlice/bookedSessionPaginationSlice';
import { getTutoringV2BookedSessions } from '../BookNewSessionTabV2/services/bookNewSessionTabV2Api';

const INITIAL_PAGE = 1;
const DEFAULT_TOTAL = 3000;

const ViewBookedSessionsTabV2 = () => {
  const dispatch = useDispatch();
  const { NotificationPopup, handleErrorResponse } = useNotification();

  // Redux selectors
  const subjects = useSelector((state) => state?.subjectsV2);
  const bookedSessionFilters = useSelector(
    (state) => state.bookedSessionFilters,
  );
  const bookedSessionPagination = useSelector(
    (state) => state?.bookedSessionPaginationKeyList?.bookedSessionPagination,
  );
  const dataGridCounts = useSelector(
    (state) => state?.totalDataGridCounts?.dataGridCounts,
  );

  const { subject, earliestDate, latestDate, ascending, includeCancelled } =
    bookedSessionFilters;

  // Local state
  const [rowData, setRowData] = useState([]);
  const [student, setStudent] = useState(null);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [bookedSessionInfo, setBookedSessionInfo] = useState({});
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [bookedSessionPageKey, setBookedSessionPageKey] = useState('');
  const [bookedSessionViewDialog, setBookedSessionViewDialog] = useState(false);
  const [bookedSessionList, setBookedSessionList] = useState({
    rows: [],
    count: 0,
    isLoading: false,
    page: INITIAL_PAGE,
    total: DEFAULT_TOTAL,
    nextPaginationKey: '',
    currentPaginationKey: '',
    pageSize: intialPageSize,
  });

  const { pageSize, page, nextPaginationKey, count } = bookedSessionList;
  const totalNumber = useMemo(
    () => dataGridCounts?.reduce((acc, curr) => acc + curr, 0) || 0,
    [dataGridCounts],
  );

  // Date formatting
  const formatDateYMD = useCallback((date) => {
    return date && isValid(new Date(date))
      ? format(new Date(date), dateFormatYMD)
      : '';
  }, []);

  const earliestDateYMD = useMemo(
    () => formatDateYMD(earliestDate),
    [earliestDate, formatDateYMD],
  );
  const latestDateYMD = useMemo(
    () => formatDateYMD(latestDate),
    [latestDate, formatDateYMD],
  );

  // Default form values
  const defaultValues = useMemo(
    () => ({
      subject: subject || '',
      ascending: ascending,
      studentId: student?.studentId || '',
      includeCancelled: includeCancelled,
      earliestDate: earliestDate || format(new Date(), dateFormatYMD),
      latestDate: latestDate || format(addDays(new Date(), 14), dateFormatYMD),
    }),
    [
      subject,
      ascending,
      latestDate,
      earliestDate,
      includeCancelled,
      student?.studentId,
    ],
  );

  // Form initialization
  const { reset, control, register } = useForm({
    defaultValues,
    mode: 'onChange',
  });

  // Memoized options
  const subjectOptions = useMemo(
    () =>
      subjects?.map((item) => ({
        id: item.subjectId,
        value: item.name,
      })) || [],
    [subjects],
  );

  // Reset pagination on mount
  useEffect(() => {
    if (page === INITIAL_PAGE) {
      dispatch(resetDataGridCounts());
      dispatch(setDataGridCounts(pageSize));
      dispatch(resetBookedSessionPagination());
    }
  }, [dispatch, page, pageSize]);

  // Update total when count changes
  useEffect(() => {
    if (!bookedSessionList?.isLoading && count < pageSize) {
      setBookedSessionList((prev) => ({
        ...prev,
        total: totalNumber,
      }));
    }
  }, [count, pageSize, totalNumber, bookedSessionList?.isLoading]);

  // Reset form when filters change
  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  // Fetch data when filters change
  useEffect(() => {
    const validLatest = !latestDate || isValid(new Date(latestDate));
    const validEarliest = !earliestDate || isValid(new Date(earliestDate));

    if (validEarliest && validLatest) {
      fetchBookedSessions();
    }
  }, [
    subject,
    student,
    pageSize,
    ascending,
    latestDate,
    earliestDate,
    includeCancelled,
    bookedSessionPageKey,
  ]);

  // API call
  const fetchBookedSessions = useCallback(async () => {
    setBookedSessionList((prev) => ({ ...prev, isLoading: true }));
    try {
      const response = await getTutoringV2BookedSessions(
        pageSize,
        bookedSessionPageKey,
        earliestDateYMD,
        latestDateYMD,
        student?.studentId,
        subject,
        ascending,
        includeCancelled,
      );

      setBookedSessionList((prev) => ({
        ...prev,
        isLoading: false,
        count: response?.count,
        rows: getBookedSessionV2ListData(response) || [],
        nextPaginationKey: response?.nextPaginationKey || '',
        currentPaginationKey: response?.currentPaginationKey || '',
      }));
    } catch (error) {
      handleErrorResponse(error?.response?.data?.message);
      setBookedSessionList((prev) => ({ ...prev, isLoading: false }));
    }
  }, [
    subject,
    pageSize,
    ascending,
    latestDateYMD,
    earliestDateYMD,
    includeCancelled,
    student?.studentId,
    handleErrorResponse,
    bookedSessionPageKey,
  ]);

  // Reset to first page
  const resetToFirstPage = useCallback(() => {
    dispatch(resetBookedSessionPagination());
    dispatch(resetDataGridCounts());
    dispatch(setDataGridCounts(pageSize));
    setBookedSessionList((prev) => ({
      ...prev,
      page: INITIAL_PAGE,
      total: DEFAULT_TOTAL,
    }));
    setBookedSessionPageKey('');
  }, [dispatch, pageSize]);

  // Filter handlers
  const createFilterHandler = useCallback(
    (action) => (e) => {
      dispatch(action(e.target.value));
      resetToFirstPage();
    },
    [dispatch, resetToFirstPage],
  );

  const handleSubject = createFilterHandler(setSubject);
  const handleAscending = createFilterHandler(setAscending);
  const handleLatestDate = createFilterHandler(setLatestDate);
  const handleEarliestDate = createFilterHandler(setEarliestDate);
  const handleIncludeCancelled = createFilterHandler(setIncludeCancelled);

  const handleClearFilters = useCallback(() => {
    setStudent(null);
    resetToFirstPage();
    dispatch(resetBookedSessionFilter());
  }, [dispatch, resetToFirstPage]);

  // Pagination handlers
  const handelPageChange = useCallback(
    (newPage) => {
      setBookedSessionList((prev) => ({
        ...prev,
        page: newPage + 1,
      }));

      const paginationIdValue = {
        id: newPage,
        value: nextPaginationKey,
      };

      if (bookedSessionPagination?.find((item) => item?.id === newPage)) {
        const getPreviousPaginationKey = () => {
          const paginationKey = bookedSessionPagination?.find(
            (item) => item?.id === newPage,
          );
          return paginationKey?.value;
        };
        const previousPaginationKey = getPreviousPaginationKey();
        dispatch(setBookedSessionPaginationPreviousKey(previousPaginationKey));
        setBookedSessionPageKey(previousPaginationKey);
      } else {
        dispatch(setDataGridCounts(count));
        setBookedSessionPageKey(nextPaginationKey);
        dispatch(setBookedSessionPagination(paginationIdValue));
      }

      if (newPage < 1) {
        setBookedSessionList((prev) => ({
          ...prev,
          total: DEFAULT_TOTAL,
        }));
        setBookedSessionPageKey('');
        dispatch(resetDataGridCounts());
        dispatch(setDataGridCounts(pageSize));
        dispatch(resetBookedSessionPagination());
      }
    },
    [count, pageSize, dispatch, nextPaginationKey, bookedSessionPagination],
  );

  const handleOnPageSizeChange = useCallback(
    (newPageSize) => {
      if (count < pageSize) {
        setBookedSessionList((prev) => ({
          ...prev,
          page: INITIAL_PAGE,
          total: totalNumber,
          pageSize: newPageSize,
        }));
      } else {
        setBookedSessionList((prev) => ({
          ...prev,
          page: INITIAL_PAGE,
          total: DEFAULT_TOTAL,
          pageSize: newPageSize,
        }));
        dispatch(resetBookedSessionPagination());
        dispatch(resetDataGridCounts());
        dispatch(setDataGridCounts(newPageSize));
      }
    },
    [count, pageSize, totalNumber, dispatch],
  );

  const handleStatus = useCallback((params) => {
    setRowData(params?.row);
    setIsStatusDialogOpen(true);
  }, []);

  const handleCancel = useCallback((params) => {
    console.log('Cancel clicked', params.row);
  }, []);

  const handleViewBookedInfo = useCallback(async (params) => {
    const { row } = params;
    try {
      setIsFormLoading(true);
      const sessionData = await getV2Session(row.sessionId);
      setBookedSessionInfo(sessionData);
      setBookedSessionViewDialog(true);
      setIsFormLoading(false);
    } catch (error) {
      setIsFormLoading(false);
      setBookedSessionViewDialog(false);
      handleErrorResponse(error?.response?.data?.message);
    }
  }, []);

  const columns = useMemo(
    () =>
      bookedSessionV2Columns(handleStatus, handleCancel, handleViewBookedInfo),
    [handleStatus, handleCancel, handleViewBookedInfo],
  );

  return (
    <>
      {showBackdropLoader(isFormLoading)}
      <MDBox mb={4.5} />

      <GridWrapper container spacing={2}>
        <Grid item xs={12}>
          <MDTypography variant="h5" fontWeight="medium">
            Tutoring Sessions
          </MDTypography>
        </Grid>

        <Grid item xs={6}>
          <StudentSearch value={student} onSelect={setStudent} />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormInputDropdown
            type="text"
            name="subject"
            label="Subject"
            control={control}
            options={subjectOptions}
            register={register('subject', {
              onChange: handleSubject,
            })}
            defaultValue={defaultValues?.subject}
          />
        </Grid>
      </GridWrapper>

      <GridWrapper container spacing={2}>
        <Grid item xs={12} sm={6} md={2.4}>
          <FormInputDate
            control={control}
            name="earliestDate"
            value={earliestDate}
            label="Earliest Date"
            register={register('earliestDate', {
              onChange: handleEarliestDate,
            })}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <FormInputDate
            name="latestDate"
            control={control}
            value={latestDate}
            label="Latest Date"
            register={register('latestDate', {
              onChange: handleLatestDate,
            })}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <FormInputDropdown
            type="text"
            name="ascending"
            label="Show Soonest First"
            control={control}
            options={booleanOptions}
            register={register('ascending', {
              onChange: handleAscending,
            })}
            defaultValue={defaultValues?.ascending}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <FormInputDropdown
            type="text"
            control={control}
            name="includeCancelled"
            options={booleanOptions}
            label="Include Cancelled"
            register={register('includeCancelled', {
              onChange: handleIncludeCancelled,
            })}
            defaultValue={defaultValues?.includeCancelled}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          {getButtonSpinner(false, handleClearFilters, 'Clear Filters')}
        </Grid>
      </GridWrapper>

      <DeltaDataGrid
        columns={columns}
        listData={bookedSessionList}
        handelPageChange={handelPageChange}
        setListData={handleOnPageSizeChange}
      />

      <BookedSessionStatusV2Dialog
        rowData={rowData}
        open={isStatusDialogOpen}
        setOpen={setIsStatusDialogOpen}
        fetchBookedSessions={fetchBookedSessions}
      />

      <BookedSessionInfoV2Dialog
        open={bookedSessionViewDialog}
        setOpen={setBookedSessionViewDialog}
        bookedSessionInfo={bookedSessionInfo}
      />
      <NotificationPopup />
    </>
  );
};

export default ViewBookedSessionsTabV2;
